import React, { useContext, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { LayoutContext } from "../../context/LayoutContext";
import BaseWrapper from "../../components/ui/BaseWrapper";
import BaseSelect from "../../components/ui/BaseSelect";
import DateRangeShortcutPicker from "../../components/ui/DateRangeShortcutPicker";

import KpiCards from "./KpiCards";
import BreakdownChart from "./BreakdownChart";
import ChartPanel from "./ChartPanel";
import WorldMapChart from "../../components/ui/WorldMapChart";
import { CustomerInvoiceStatusLabel } from "../../utils/enum";
import CountryRanking from "../../components/ui/CountryRanking";
import StatusDistributionChart from "./StatusDistributionChart";
import EntityPerformanceChart from "./EntityPerformanceChart";
import {
  fetchCustomerDashboard,
  fetchCustomerDashboardFilterOptions,
} from "../../services/api/customerDashboard";

const ALL_VALUE = "";

const toDateStr = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const now = new Date();
// Previous, current and next month — the same window the API defaults to when
// no dates are sent. Day 0 of a month is the last day of the one before it, and
// Date rolls the month over a year boundary on its own.
const defaultStart = toDateStr(new Date(now.getFullYear(), now.getMonth() - 1, 1));
const defaultEnd = toDateStr(new Date(now.getFullYear(), now.getMonth() + 2, 0));

const CustomerDashboard = () => {
  const { isBannerVisible } = useContext(LayoutContext);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [entityId, setEntityId] = useState(ALL_VALUE);

  // The option list comes from the API, so it can only ever contain entities this
  // customer owns — the UI never assembles it from ids of its own.
  const { data: filterOptionsResult } = useQuery({
    queryKey: ["customer-dashboard-filterOptions"],
    queryFn: fetchCustomerDashboardFilterOptions,
    staleTime: 300_000,
  });

  const filterOptions = filterOptionsResult?.data;

  const entityOptions = useMemo(
    () => [
      { value: ALL_VALUE, label: "All My Entities" },
      ...(filterOptions?.entities?.map((entity) => ({
        value: String(entity.id),
        label: entity.name,
      })) ?? []),
    ],
    [filterOptions],
  );

  const {
    data: dashboardResult,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["customer-dashboard", startDate, endDate, entityId],
    queryFn: () =>
      fetchCustomerDashboard({
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
        ...(entityId ? { entityId } : {}),
      }),
    staleTime: 60_000,
  });

  const dashboard = dashboardResult?.data;
  const isParentEntity = dashboard?.scope?.isParentEntity;

  // The map and the ranking beside it are the same components the Work Order
  // dashboard uses, and both read { country, count }.
  const countryData = useMemo(
    () =>
      (dashboard?.countryBreakdown ?? []).map((row) => ({
        country: row.country,
        count: row.total,
      })),
    [dashboard],
  );

  return (
    <div
      className={`bg-[#F6F7FB] sm:fixed sticky 2xl:w-[95.2%] xl:w-[93.2%] lg:w-[90.6%] md:w-[87.5%] sm:w-[95%] w-full overflow-y-auto transition-all ${
        isBannerVisible ? "top-[106px] h-[calc(100vh-106px)]" : "top-[56px] h-[calc(100vh-56px)]"
      }`}
    >
      <div className="border-b border-zinc-200 bg-white flex-shrink-0">
        <BaseWrapper>
          <div className="flex items-center justify-between py-[7px] gap-3 flex-wrap">
            <h3 className="font-light">Dashboard</h3>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Only rendered when there is more than one entity to choose from —
                  a child customer has nothing to filter between. */}
              {entityOptions.length > 2 && (
                <BaseSelect
                  options={entityOptions}
                  className="w-[190px] p-1"
                  required
                  labelShrink={false}
                  value={entityId}
                  onChange={(event) => setEntityId(event.target.value)}
                  placeholder="All My Entities"
                />
              )}

              <DateRangeShortcutPicker
                startDate={startDate}
                endDate={endDate}
                onChange={(start, end) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
              />
            </div>
          </div>
        </BaseWrapper>
      </div>

      <BaseWrapper className="py-5 flex flex-col gap-5">
        <>
          {error ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
              <i className="fa-solid fa-triangle-exclamation text-3xl text-red-400 mb-3" />
              <p className="text-sm text-gray-600">We couldn’t load your dashboard.</p>
              <p className="text-[12px] text-gray-400 mt-1">
                Please refresh the page and try again.
              </p>
            </div>
          ) : (
            <>
              <KpiCards
                summary={dashboard?.summary}
                trends={dashboard?.trends ?? []}
                isLoading={isLoading}
              />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <BreakdownChart
                  title="Invoice Status"
                  subtitle="Your invoices by state"
                  icon="fa-solid fa-file-invoice"
                  accent="#16A34A"
                  accentBg="#F0FDF4"
                  type="pie"
                  height={320}
                  help="Invoice count by status for invoices issued within the selected date range, aggregated across all currencies."
                  // The API already withholds invoices still being prepared;
                  // dropping unmapped statuses here keeps the guarantee local,
                  // so nothing internal can ever surface under a borrowed name.
                  data={(dashboard?.invoiceStatusDistribution ?? [])
                    .filter((row) => CustomerInvoiceStatusLabel[row.status])
                    .map((row) => ({
                      label: CustomerInvoiceStatusLabel[row.status],
                      value: row.count,
                    }))}
                  emptyMessage="No invoices in this period."
                  isLoading={isLoading}
                  error={error}
                />
                <StatusDistributionChart
                  statusDistribution={dashboard?.statusDistribution ?? []}
                  isLoading={isLoading}
                  error={error}
                />
              </div>

              {/* A single-entity customer gets no breakdown — there is nothing to
                  compare against, and siblings are never in scope. */}
              {(isLoading || isParentEntity) && (
                <EntityPerformanceChart
                  entityPerformance={dashboard?.entityPerformance ?? []}
                  isLoading={isLoading}
                  error={error}
                />
              )}

              <ChartPanel
                title="Work Orders by Country"
                subtitle="Geographic distribution of your work"
                icon="fa-solid fa-earth-americas"
                accent="#0891B2"
                accentBg="#ECFEFF"
                help="Work order volume by country, based on the site address recorded against each work order. Darker bands carry more work; drag to pan and use the buttons to zoom. Work with no address on file is listed as N/A in the ranking and left off the map."
                isLoading={isLoading}
                error={error}
                isEmpty={countryData.length === 0}
                emptyMessage="No site locations recorded."
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <WorldMapChart data={countryData} />
                  </div>
                  <div className="flex flex-col">
                    <p className="text-[11px] text-gray-400 uppercase tracking-wide font-semibold mb-3">
                      Top Countries
                    </p>
                    <CountryRanking data={countryData} />
                  </div>
                </div>
              </ChartPanel>
            </>
          )}
        </>
      </BaseWrapper>
    </div>
  );
};

export default CustomerDashboard;
